import { useEffect } from 'react'
import usePurchaseCreditsDialogViewModal from './PurchaseCreditsDialogViewModel'
import { titleCase } from '@components/shared/utils'
import cn from 'clsx'

import CustomModal from '@/components/shared/CustomModal/CustomModal'

const pricingStep = 50
/** @type {number[]} */
const pricingSteps = Array.from({ length: 10 }, 
    (_, i) => (i + 1) * pricingStep)

/**
 * Dialog for purchasing credits via Stripe
 * 
 * Important: if you are not seeing your balance update after
 * a successful purchase, make sure you have started the API
 * server with the Stripe webhooks key set. See API docs.
 * 
 * @param {string|null} [className]
 * @param {User} user
 * @param {Organization} organization
 * @param {StripeCard[]} paymentCards
 * @returns {JSX.Element}
 * @constructor
 */
const PurchaseCreditsDialog = ({
    className,
    user,
    organization,
    paymentCards,
}) => {
    
    const vm = usePurchaseCreditsDialogViewModal()
    
    useEffect(() => {
        
        if (!paymentCards?.length) return
        
        if (!vm.selectedCardId)
            vm.setSelectedCardId(paymentCards[0].id)
        
    }, [paymentCards, vm.selectedCardId])
    
    return (
        
        <CustomModal
            className={cn('PurchaseCreditsDialog', className)}
            open={vm.open}
            modalHandler={vm.closeDialog}
            center>
            
            <div className="modal-header">
                <h3 className="text-2xl">Add Funds</h3>
            </div>
            
            <div className="text-base modal-body">
                
                <div className="modal-body-inner overflow-h-auto">
                    
                    <div className="">
                        <div className="flex items-center gap-2">
                            <div><b>Organization:</b></div>
                            <div>{organization?.name}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div><b>Billing Account:</b></div>
                            <div>{user?.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div><b>Current Balance:</b></div>
                            <div>${vm.currBalance.toFixed(2)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div><b>New Balance:</b></div>
                            <div>${
                                (vm.currBalance + vm.amount)
                                    .toFixed(2)
                            }</div>
                        </div>
                    </div>
                    
                    <div className="mt-8 flex items-center gap-4">
                        
                        <select
                            className="select select-bordered"
                            value={vm.selectedAccountType}
                            onChange={e => vm.setSelectedAccountType(e.target.value)}>
                            <option value={vm.ACCOUNT_TYPES.User}>
                                Personal Account
                            </option>
                            <option value={vm.ACCOUNT_TYPES.Organization}>
                                Organization Account
                            </option>
                        </select>
                        
                        <select
                            className="select select-bordered"
                            value={vm.selectedCardId}
                            onChange={e => vm.setSelectedCardId(e.target.value)}>
                            {paymentCards?.map((card, i) => (
                                <option key={`stripe-card-${i}`} value={card.name}>
                                    {titleCase(card.card.brand, true)} | Last digits: {card.card.last4} | Exp:{' '}
                                    {`${card.card.exp_month}/${card.card.exp_year}`}
                                </option>
                            ))}
                        </select>
                    
                    </div>
                    
                    <div className="mt-8">
                        <input
                            className="range"
                            type="range"
                            min={pricingSteps[0]}
                            max={pricingSteps[pricingSteps.length - 1]}
                            step={pricingStep}
                            value={vm.amount}
                            onChange={e => vm.setAmount(parseInt(e.target.value, 10))}/>
                        <div className="w-full flex justify-between text-xs px-2">
                            {pricingSteps.map(step => (
                                <div
                                    key={`pricing-step-${step}`}
                                    className={cn('flex flex-col justify-center text-center', {
                                        'text-accent': vm.amount === step,
                                    })}>
                                    <span>|</span>
                                    <span>${step}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                
                </div>
            
            </div>
            
            <div className="flex justify-end mt-12">
                <div className="flex justify-between modal-footer">
                    <div>
                        <button
                            className="mr-3 btn btn-primary btn-outline"
                            disabled={vm.loading}
                            onClick={vm.closeDialog}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={vm.loading}
                            onClick={vm.handleSubmit}>
                            Complete Purchase
                            {vm.loading && <span className="loading loading-spinner loading-sm" />}
                        </button>
                    </div>
                </div>
            </div>
        
        </CustomModal>
        
    )
    
}

export default PurchaseCreditsDialog
